import { keyBy } from 'lodash-es'
import { useCallback } from 'react'
import {
    DiscountContractWithCustom,
    ISelectItemInCart,
    OrderFulfilmentType,
    OrderItemViewModel,
} from 'store/models/sellModels'
import { ICartContext } from '../../../views/Sale/SaleUI/Cart/CartContext'
import { DiscountPopupContextProps } from '../DiscountPopup/context/DiscountPopupContext'
import { useSaleDependenciesContext } from 'views/Sale/contexts/SaleDependenciesContext'
import { OrderChannel } from 'constant/OrderChannels'
import { TModifyDialogDependencies } from '../ModifierDialog/ModifyDialogDependenciesContext'
import { TOldSaleData } from 'views/Sale/hooks/useSaleSelectReducer/useSaleSelectReducer'
import { DiscountContract } from 'models'
import { usePosContext } from 'views/Sale/contexts/PosProvider'

const useGetSellDeps = () => {
    const {
        menus,
        modifierList,
        unavailableProducts,
        modifierOptionsMap,
        variantGroupsMap,
        itemsMap,
        priceListData,
        comboChoicesMap,
        discounts,
        modifierGroupsMap,
        variantOptionModifierMap,
        unavailableVariantOptionIds,
        entityModifiersMap,
    } = useSaleDependenciesContext()
    const {
        strictTimeAvailabilityOnSales,
        isCalculateCompAfterDiscount,
    } = usePosContext()
    const getCartDependencies: (
        selectedItemInCart: ISelectItemInCart | undefined,
        onSelectDiscountOrComp: (selectItem: ISelectItemInCart) => void,
        removedData: TOldSaleData,
    ) => ICartContext = useCallback(
        (selectedItemInCart, onSelectDiscountOrComp, removedData) => {
            return {
                comboChoicesMap,
                priceListData,
                unavailableProducts,
                itemsMap,
                modifierOptionsMap,
                modifierList,
                variantGroupsMap,
                selectedItemInCart,
                onSelectDiscountOrComp,
                removedData,
                strictTimeAvailability: strictTimeAvailabilityOnSales,
                menus,
                entityModifiersMap,
            }
        },
        [
            comboChoicesMap,
            priceListData,
            unavailableProducts,
            itemsMap,
            modifierOptionsMap,
            modifierList,
            variantGroupsMap,
            strictTimeAvailabilityOnSales,
            menus,
            entityModifiersMap,
        ],
    )
    const getDiscountPopupDependencies: (
        entireOrderDiscounts: DiscountContractWithCustom[],
        orderItems: OrderItemViewModel[],
        orderChannelID: OrderChannel,
        currentFulfillmentType: OrderFulfilmentType,
        onDismiss: () => void,
        onApply?: (
            items: OrderItemViewModel[],
            entireDiscounts: DiscountContract[],
            resetEntire?: boolean,
            pin?: string,
        ) => void,
    ) => DiscountPopupContextProps = useCallback(
        (
            selectEntireOrderDiscounts,
            orderItems,
            orderChannelID,
            currentFulfillmentType,
            onDismiss,
            onApply,
        ) => {
            return {
                discounts,
                selectEntireOrderDiscounts,
                orderItems:
                    // No Deposit-and-Credit items for Discount
                    orderItems.filter(
                        (oi) => !Boolean(oi.item.baseDepositAndCreditItemId),
                    ),
                orderChannelID,
                currentFulfillmentType,
                priceListData,
                itemMaps: itemsMap,
                categoryMap: keyBy(
                    menus.flatMap((a) => a.categories),
                    (a) => a.categoryId,
                ),
                onDismiss,
                onApply,
            }
        },
        [discounts, priceListData, itemsMap, menus],
    )

    const getModifyDialogDeps: (
        useAssignTo: boolean,
    ) => TModifyDialogDependencies = useCallback(
        (useAssignTo) => ({
            modifierList,
            priceListData,
            comboChoicesMap,
            modifierGroupsMap,
            unavailableProducts,
            modifierOptionsMap,
            variantGroupsMap,
            itemsMap,
            useAssignTo: useAssignTo,
            variantOptionModifierMap,
            strictTimeAvailabilityOnSales: strictTimeAvailabilityOnSales,
            unavailableVariantOptionIds,
            isCalculateCompAfterDiscount,
        }),
        [
            modifierList,
            priceListData,
            comboChoicesMap,
            modifierGroupsMap,
            unavailableProducts,
            modifierOptionsMap,
            variantGroupsMap,
            itemsMap,
            variantOptionModifierMap,
            strictTimeAvailabilityOnSales,
            unavailableVariantOptionIds,
            isCalculateCompAfterDiscount,
        ],
    )
    return {
        getCartDependencies,
        getDiscountPopupDependencies,
        getModifyDialogDeps,
    }
}

export default useGetSellDeps
